import Loading from 'components/loading';
import PageLoading from 'components/pageLoading';
import i18n from 'configs/i18next';
import { PathLogout } from 'context/path-logout';
import { ProtectedRoute } from 'context/protected-route';
import AppLayout from 'layout/app-layout';
import { WelcomeLayout } from 'layout/welcome-layout';
import Providers from 'providers';
import { Suspense, useEffect, useState } from 'react';
import {
  Route,
  BrowserRouter as Router,
  Routes,
  Navigate,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AllRoutes } from 'routes';
import informationService from 'services/rest/information';
import GlobalSettings from 'views/global-settings/global-settings';
import Login from 'views/login';
import NotFound from 'views/not-found';
import Welcome from 'views/welcome/welcome';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { cacheChecker } from './cacheChecker';
import {
  fetchRestSettings,
  fetchSettings,
} from './redux/slices/globalSettings';
import languagesService from 'services/languages';
import { directionChange } from 'redux/slices/theme';
import { setLangugages, setDefaultLanguage } from 'redux/slices/formLang';

const App = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth, shallowEqual);

  const [loading, setLoading] = useState(false);

  const fetchUserSettings = (role) => {
    switch (role) {
      case 'admin':
        dispatch(fetchSettings({}));
        break;
      case 'seller':
        dispatch(fetchRestSettings({ seller: true }));
        break;
      default:
        dispatch(fetchRestSettings({}));
    }
  };

  useEffect(() => {
    cacheChecker();
    return () => {};
  }, []);

  useEffect(() => {
    bootstrapLocale();
    return () => {};
  }, []);

  useEffect(() => {
    fetchUserSettings(user?.role || '');
    return () => {};
  }, [user?.role]);

  // Load all active languages, set the current language's translations,
  // and apply the correct direction (RTL for Arabic, LTR otherwise).
  // Strategy: Local file (/locales/{{lng}}/translation.json) is the SOURCE OF TRUTH.
  // API translations are merged ONLY for keys that don't exist in the local file
  // (because the API has incomplete translations, mostly English fallback).
  const bootstrapLocale = async () => {
    setLoading(true);
    try {
      const { data: langs } = await languagesService.getAllActive();
      if (Array.isArray(langs)) {
        dispatch(setLangugages(langs));
        const defaultLang =
          langs.find((item) => item?.default)?.locale || 'en';
        dispatch(setDefaultLanguage(defaultLang));

        // Determine current language:
        //   1. localStorage (set by previous LangModal selection)
        //   2. Default language from backend
        const stored =
          typeof localStorage !== 'undefined'
            ? localStorage.getItem('i18nextLng')
            : null;
        const current =
          langs.find((item) => item.locale === stored) || langs.find(
            (item) => item.locale === defaultLang,
          );
        const currentLocale = current?.locale || defaultLang;
        const direction = currentLocale === 'ar' || current?.backward ? 'rtl' : 'ltr';

        // Switch i18n to the current language.
        // This triggers i18next-http-backend to load /locales/{{lng}}/translation.json
        // which contains the full 1948 keys with proper translations.
        await i18n.changeLanguage(currentLocale);

        // Wait for the local file to load, then merge API translations
        // for keys NOT in the local file (additive, not overriding).
        setTimeout(async () => {
          try {
            const { data: translations } = await informationService.translations({
              lang: currentLocale,
            });
            if (translations && Object.keys(translations).length > 0) {
              const localKeys = Object.keys(
                i18n.getResourceBundle(currentLocale, 'translation') || {},
              );
              const apiOnlyKeys = {};
              for (const [key, value] of Object.entries(translations)) {
                if (!localKeys.includes(key) && value) {
                  apiOnlyKeys[key] = value;
                }
              }
              if (Object.keys(apiOnlyKeys).length > 0) {
                i18n.addResourceBundle(
                  currentLocale,
                  'translation',
                  apiOnlyKeys,
                  true,
                  true, // overwrite=false: don't clobber existing local translations
                );
              }
            }
          } catch (err) {
            console.warn('API translations merge failed (using local file only):', err);
          }
        }, 300);

        // Update direction in redux + document
        dispatch(directionChange(direction));
        const html = document.documentElement;
        html.setAttribute('lang', currentLocale);
        html.setAttribute('dir', direction);
        document.body.setAttribute('dir', direction);
        document.body.classList.remove('dir-ltr', 'dir-rtl');
        document.body.classList.add('dir-' + direction);
        try {
          localStorage.setItem('i18nextLng', currentLocale);
          localStorage.setItem('app-direction', direction);
        } catch (e) {
          console.warn('localStorage persist failed', e);
        }
      }
    } catch (err) {
      console.error('bootstrapLocale failed', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTranslations = () => {
    const params = { lang: i18n.language };
    setLoading(true);
    informationService
      .translations(params)
      .then(({ data }) =>
        i18n.addResourceBundle(i18n.language, 'translation', data),
      )
      .finally(() => setLoading(false));
  };

  return (
    <Providers>
      <Router>
        <Routes>
          <Route
            index
            path='/login'
            element={
              <PathLogout>
                <Login />
              </PathLogout>
            }
          />
          <Route
            path='/welcome'
            element={
              <WelcomeLayout>
                <Welcome />
              </WelcomeLayout>
            }
          />
          <Route
            path='/installation'
            element={
              <WelcomeLayout>
                <GlobalSettings />
              </WelcomeLayout>
            }
          />
          <Route
            path=''
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path='/' element={<Navigate to='dashboard' />} />
            {AllRoutes.map(({ path, component: Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
          </Route>
          <Route
            path='*'
            element={
              <Suspense fallback={<Loading />}>
                <NotFound />
              </Suspense>
            }
          />
        </Routes>
        <ToastContainer
          className='antd-toast'
          position='top-right'
          autoClose={2500}
          hideProgressBar
          closeOnClick
          pauseOnHover
          draggable
        />
        {loading && <PageLoading />}
      </Router>
    </Providers>
  );
};
export default App;
